import { get } from "lodash";
import { useCallback, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { useDynamicGridTranslate } from "@ryde/redux";
import {
  appLanguageSelector,
  getComputedGridFieldName,
  getGridFieldTypeNameById,
  ICustomField,
  IExtendableDto,
  parseBoolean,
  profileMetadataLanguageSelector,
} from "@ryde/shared";

import { useCsvCellTransformer } from "../../../../dynamic-forms";
import {
  IDynamicCsvColumns,
  IUseCsvDefDynamic,
  IUseCsvDefDynamicResponse,
} from "./models";

export const useCsvDefDynamic = ({
  entity,
  gridControls,
}: IUseCsvDefDynamic): IUseCsvDefDynamicResponse => {
  const { csvCellTransformer } = useCsvCellTransformer({ entity });
  const { getGridLabelComputed, getGridLabelTranslationKeyComputed } =
    useDynamicGridTranslate();
  const appLanguage = useRecoilValue(appLanguageSelector);
  const profileLanguage = useRecoilValue(profileMetadataLanguageSelector);

  const getColumnMetadata = useCallback(
    ({
      control,
      language,
    }: {
      control: ICustomField;
      language: string | undefined;
    }) => {
      const { id, gridOrderable, gridFieldTypeId } = control;
      return {
        customFieldId: id,
        gridFieldTypeId,
        orderable: parseBoolean(gridOrderable),
        displayName: getGridLabelComputed(entity, control),
        translationKey: getGridLabelTranslationKeyComputed(entity, control),
        language,
      };
    },
    [entity, getGridLabelComputed, getGridLabelTranslationKeyComputed],
  );

  const getCsvCellTransformer = useCallback(
    (
      control: ICustomField,
      row: IExtendableDto,
      computedGridFieldName: string,
    ) => {
      const { gridAttributes, gridFieldTypeId } = control;
      const value = get(row, computedGridFieldName);

      const controlInfoName = gridFieldTypeId
        ? getGridFieldTypeNameById(gridFieldTypeId)
        : undefined;

      return csvCellTransformer({
        value,
        ...control,
        controlInfoName,
        rowId: row?.id || 0,
        rowOriginal: row,
        truncateChars: gridAttributes?.gridTruncateChars,
      });
    },
    [csvCellTransformer],
  );

  const dynamicCsvColumns: IDynamicCsvColumns[] = useMemo(
    () =>
      gridControls.map(control => {
        const id = getComputedGridFieldName(control);

        return {
          id,
          header: getGridLabelComputed(entity, control),
          getCellValue: (row: IExtendableDto) =>
            getCsvCellTransformer(control, row, id),
          meta: getColumnMetadata({
            control,
            language: profileLanguage || appLanguage,
          }),
          gridCustomField: control,
        };
      }),
    [
      appLanguage,
      entity,
      getColumnMetadata,
      getCsvCellTransformer,
      getGridLabelComputed,
      gridControls,
      profileLanguage,
    ],
  );

  return { dynamicCsvColumns };
};
